import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import * as d3 from "d3";

const API_BASE = 'https://futurista-api-v3-520601931215.us-west1.run.app';
const API_KEY  = process.env.REACT_APP_API_KEY;

// ── Paleta Zafiro ─────────────────────────────────────────────────────────
const PESTEL_COLORS = {
  "Político":    "#35627A",
  "Económico":   "#059669",
  "Social":      "#B46258",
  "Tecnológico": "#A6A9D0",
  "Ambiental":   "#8E9A98",
  "Legal":       "#E5AEA9",
};

const PESTEL_TEXT = {
  "Político":    "#264a5e",
  "Económico":   "#065f46",
  "Social":      "#8a3f35",
  "Tecnológico": "#5557a0",
  "Ambiental":   "#4a5a58",
  "Legal":       "#8a6a58",
};

const PESTEL_ICONS = {
  "Político":    "🏛️",
  "Económico":   "📊",
  "Social":      "👥",
  "Tecnológico": "⚙️",
  "Ambiental":   "🌿",
  "Legal":       "⚖️",
};

const PESTEL_GRADIENT = {
  "Político":    "linear-gradient(135deg, #35627A22, #35627A44)",
  "Económico":   "linear-gradient(135deg, #05996922, #05996944)",
  "Social":      "linear-gradient(135deg, #B4625822, #B4625844)",
  "Tecnológico": "linear-gradient(135deg, #A6A9D022, #A6A9D044)",
  "Ambiental":   "linear-gradient(135deg, #8E9A9822, #8E9A9844)",
  "Legal":       "linear-gradient(135deg, #E5AEA922, #E5AEA944)",
};

// FIX 3: Las 6 categorías PESTEL fijas — no dependen de los datos
const PESTEL_CATS = Object.keys(PESTEL_COLORS);

const RINGS = [
  { label: "Cluster central",  r: 0.33 },
  { label: "Cluster medio",    r: 0.66 },
  { label: "Cluster externo",  r: 1.00 },
];

// ── Resumen LLM ───────────────────────────────────────────────────────────
async function fetchResumen(signal) {
  // Usar resumen del ETL directamente — sin llamada al API
  if (signal.resumen && signal.resumen.length > 10) {
    return signal.resumen;
  }
  return "Resumen no disponible para esta señal.";
}

// ── Panel lateral ─────────────────────────────────────────────────────────
const PESTEL_IMAGES = {
  "Político":    "https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=400&auto=format&fit=crop",
  "Económico":   "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&auto=format&fit=crop",
  "Social":      "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&auto=format&fit=crop",
  "Tecnológico": "https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&auto=format&fit=crop",
  "Ambiental":   "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&auto=format&fit=crop",
  "Legal":       "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400&auto=format&fit=crop",
};

function SignalPanel({ signal, onClose }) {
  if (!signal) return null;

  const color  = PESTEL_COLORS[signal.pestel_category]    || "#35627A";
  const textC  = PESTEL_TEXT[signal.pestel_category]      || "#264a5e";
  const icon   = PESTEL_ICONS[signal.pestel_category]     || "📌";
  const grad   = PESTEL_GRADIENT[signal.pestel_category]  || PESTEL_GRADIENT["Político"];
  const isOpp  = signal.classification === "Oportunidad";
  const impact = Math.round((+signal.impact_probability || 0) * 100);
  const imgSrc = (signal.imagen_url && signal.imagen_url.length > 5)
    ? signal.imagen_url
    : PESTEL_IMAGES[signal.pestel_category] || PESTEL_IMAGES["Económico"];

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0,
      width: 360, background: "#fff",
      borderLeft: "1px solid #e2e5e4",
      boxShadow: "-4px 0 24px rgba(53,98,122,0.12)",
      display: "flex", flexDirection: "column",
      zIndex: 1000,
      animation: "slideIn 0.2s ease",
    }}>
      <style>{`
        @keyframes slideIn { from { transform: translateX(360px); } to { transform: translateX(0); } }
      `}</style>

      {/* Imagen */}
      <div style={{ position: "relative", height: 180, flexShrink: 0, overflow: "hidden" }}>
        <img
          src={imgSrc}
          alt={signal.Title}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={(e) => { e.target.src = PESTEL_IMAGES["Económico"]; }}
        />
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.5))",
        }} />
        <button onClick={onClose} style={{
          position: "absolute", top: 12, right: 12,
          background: "rgba(255,255,255,0.9)", border: "none",
          borderRadius: "50%", width: 30, height: 30,
          cursor: "pointer", fontSize: 16, color: "#333",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>×</button>
        <div style={{
          position: "absolute", bottom: 12, left: 12, right: 12,
          display: "flex", gap: 6, flexWrap: "wrap",
        }}>
          <span style={{
            padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 600,
            background: isOpp ? "rgba(53,98,122,0.85)" : "rgba(180,98,88,0.85)",
            color: "#fff",
          }}>{isOpp ? "↑ Oportunidad" : "↓ Riesgo"}</span>
          <span style={{
            padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 600,
            background: "rgba(0,0,0,0.5)", color: "#fff",
          }}>{icon} {signal.pestel_category}</span>
        </div>
      </div>

      {/* Contenido */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

        {/* Título */}
        {signal.url && signal.url.length > 5 ? (
          <a href={signal.url} target="_blank" rel="noopener noreferrer"
            style={{
              fontSize: 14, fontWeight: 700, color: "#111", lineHeight: 1.5,
              textDecoration: "none", display: "block", marginBottom: 12,
            }}>
            {signal.Title} <span style={{ color: "#35627A" }}>↗</span>
          </a>
        ) : (
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111", lineHeight: 1.5, marginBottom: 12 }}>
            {signal.Title}
          </div>
        )}

        {/* Fuente y fecha */}
        <div style={{
          fontSize: 11, color: "#8E9A98", marginBottom: 16,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span>📰 {signal.Source}</span>
          <span>·</span>
          <span>{signal.Date}</span>
        </div>

        {/* Métricas */}
        {[
          { label: "Horizonte temporal", value: signal.time_horizon },
          { label: "Escenario",          value: signal.cluster },
          { label: "Índice de impacto",  value: (+signal.impact_score || 0).toFixed(3) },
        ].map(({ label, value }) => (
          <div key={label} style={{
            display: "flex", justifyContent: "space-between",
            padding: "10px 0", borderBottom: "1px solid #f0f0f0", fontSize: 12,
          }}>
            <span style={{ color: "#8E9A98", fontWeight: 500 }}>{label}</span>
            <span style={{ color: "#111", fontWeight: 600 }}>{value || "—"}</span>
          </div>
        ))}

        {/* Probabilidad de impacto */}
        <div style={{ padding: "14px 0", borderBottom: "1px solid #f0f0f0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "#8E9A98", fontWeight: 500 }}>Probabilidad de impacto</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: isOpp ? "#264a5e" : "#8a3f35" }}>{impact}%</span>
          </div>
          <div style={{ height: 6, background: "#e2e5e4", borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${impact}%`,
              background: isOpp ? "#35627A" : "#B46258",
              borderRadius: 3, transition: "width 0.6s ease",
            }} />
          </div>
        </div>

{/* Indicadores de innovación */}
<div style={{ padding: "14px 0", borderBottom: "1px solid #f0f0f0" }}>
  <div style={{
    fontSize: 9, fontFamily: "JetBrains Mono, monospace",
    color: "#8E9A98", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10,
  }}>
    Indicadores de madurez
  </div>

  {(() => {
    // Calcular TRL, MRL, BRL
    const trl = signal.fuente_tipo === "patents" ? { val: "4-6", label: "Validado en lab/piloto", color: "#35627A" }
              : signal.fuente_tipo === "scholar"  ? { val: "1-3", label: "Investigación básica/aplicada", color: "#059669" }
              : { val: "6-9", label: "Demostrado en mercado", color: "#B46258" };

    const mrl = signal.time_horizon === "Corto plazo"  ? { val: "6-8", label: "Capacidad de producción disponible", color: "#35627A" }
              : signal.time_horizon === "Mediano plazo" ? { val: "4-6", label: "Producción en desarrollo", color: "#E5AEA9" }
              : { val: "1-3", label: "Concepto de manufactura", color: "#A6A9D0" };

    const brlScore = Math.min(9, Math.max(1, Math.round(
      ((+signal.impact_probability || 0) * 5) +
      (signal.classification === "Oportunidad" ? 2 : 0) +
      (signal.time_horizon === "Corto plazo" ? 2 : signal.time_horizon === "Mediano plazo" ? 1 : 0)
    )));
    const brl = brlScore >= 7 ? { val: brlScore, label: "Alta viabilidad comercial", color: "#059669" }
              : brlScore >= 4 ? { val: brlScore, label: "Viabilidad en desarrollo", color: "#35627A" }
              : { val: brlScore, label: "Etapa exploratoria", color: "#A6A9D0" };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* TRL */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: "#8E9A98", fontFamily: "JetBrains Mono, monospace" }}>TRL — Technology Readiness</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: trl.color }}>Nivel {trl.val}</span>
          </div>
          <div style={{ height: 4, background: "#e2e5e4", borderRadius: 2, overflow: "hidden", marginBottom: 3 }}>
            <div style={{
              height: "100%",
              width: `${(parseInt(trl.val.split("-")[1] || trl.val) / 9) * 100}%`,
              background: trl.color, borderRadius: 2,
            }} />
          </div>
          <div style={{ fontSize: 10, color: "#8E9A98" }}>{trl.label}</div>
        </div>

        {/* MRL */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: "#8E9A98", fontFamily: "JetBrains Mono, monospace" }}>MRL — Manufacturing Readiness</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: mrl.color }}>Nivel {mrl.val}</span>
          </div>
          <div style={{ height: 4, background: "#e2e5e4", borderRadius: 2, overflow: "hidden", marginBottom: 3 }}>
            <div style={{
              height: "100%",
              width: `${(parseInt(mrl.val.split("-")[1] || mrl.val) / 9) * 100}%`,
              background: mrl.color, borderRadius: 2,
            }} />
          </div>
          <div style={{ fontSize: 10, color: "#8E9A98" }}>{mrl.label}</div>
        </div>

        {/* BRL */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: "#8E9A98", fontFamily: "JetBrains Mono, monospace" }}>BRL — Business Readiness</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: brl.color }}>Nivel {brl.val}/9</span>
          </div>
          <div style={{ height: 4, background: "#e2e5e4", borderRadius: 2, overflow: "hidden", marginBottom: 3 }}>
            <div style={{
              height: "100%",
              width: `${(brl.val / 9) * 100}%`,
              background: brl.color, borderRadius: 2,
            }} />
          </div>
          <div style={{ fontSize: 10, color: "#8E9A98" }}>{brl.label}</div>
        </div>

        {/* Tipo de señal */}
        <div style={{
          padding: "6px 10px", borderRadius: 6, fontSize: 11, lineHeight: 1.5,
          background: signal.fuente_tipo === "patents" ? "rgba(53,98,122,0.06)"
                    : signal.fuente_tipo === "scholar"  ? "rgba(5,150,105,0.06)"
                    : "rgba(180,98,88,0.06)",
          color: signal.fuente_tipo === "patents" ? "#264a5e"
               : signal.fuente_tipo === "scholar"  ? "#065f46"
               : "#8a3f35",
        }}>
          {signal.fuente_tipo === "patents" && "💡 Patente tecnológica emergente. Evalúa viabilidad de licenciamiento o desarrollo propio."}
          {signal.fuente_tipo === "scholar"  && "🔭 Evidencia científica de largo plazo. Considera alianzas con centros de investigación."}
          {signal.fuente_tipo === "news"     && "⚡ Señal de mercado. Requiere respuesta estratégica inmediata o monitoreo continuo."}
        </div>
      </div>
    );
  })()}
</div>

        {/* Sectores afectados */}
        {signal.sector_afectado && signal.sector_afectado.length > 5 && (
          <div style={{ padding: "14px 0" }}>
            <div style={{
              fontSize: 9, fontFamily: "JetBrains Mono, monospace",
              color: "#8E9A98", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8,
            }}>
              Sectores afectados
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {signal.sector_afectado.split(",").map((s, i) => (
                <span key={i} style={{
                  padding: "3px 10px", borderRadius: 20,
                  fontSize: 10, fontWeight: 500,
                  background: "rgba(53,98,122,0.08)",
                  border: "1px solid rgba(53,98,122,0.2)",
                  color: "#264a5e",
                }}>{s.trim()}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Filtros ───────────────────────────────────────────────────────────────
function FilterBar({ activeFilters, onFilterChange }) {
  const toggle = (type, value) => {
    onFilterChange(prev => {
      const current = prev[type] || [];
      return {
        ...prev,
        [type]: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value]
      };
    });
  };

  const clearAll   = () => onFilterChange({ pestel: [], tipo: [] });
  const hasFilters = (activeFilters.pestel?.length || 0) + (activeFilters.tipo?.length || 0) > 0;

  return (
    <div style={{
      display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
      marginBottom: 14, padding: "10px 14px",
      background: "#fff", border: "1px solid #e2e5e4", borderRadius: 8,
    }}>
      <span style={{ fontSize: 10, color: "#8E9A98", fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase", letterSpacing: 1, marginRight: 4 }}>
        Filtrar:
      </span>

      {/* Tipo */}
      {["Oportunidad", "Riesgo"].map(tipo => {
        const active = activeFilters.tipo?.includes(tipo);
        return (
          <button key={tipo} onClick={() => toggle("tipo", tipo)} style={{
            padding: "4px 10px", borderRadius: 20, cursor: "pointer",
            fontSize: 10, fontWeight: 600, fontFamily: "JetBrains Mono, monospace",
            border: `1px solid ${tipo === "Oportunidad" ? "rgba(53,98,122,0.4)" : "rgba(180,98,88,0.4)"}`,
            background: active ? (tipo === "Oportunidad" ? "rgba(53,98,122,0.15)" : "rgba(180,98,88,0.15)") : "transparent",
            color: tipo === "Oportunidad" ? "#264a5e" : "#8a3f35",
            transition: "all .15s",
          }}>
            {tipo === "Oportunidad" ? "↑" : "↓"} {tipo}
          </button>
        );
      })}

      <div style={{ width: 1, height: 16, background: "#e2e5e4" }} />

      {/* FIX 3: Siempre las 6 categorías fijas */}
      {PESTEL_CATS.map(cat => {
        const active = activeFilters.pestel?.includes(cat);
        const color  = PESTEL_COLORS[cat];
        const textC  = PESTEL_TEXT[cat];
        return (
          <button key={cat} onClick={() => toggle("pestel", cat)} style={{
            padding: "4px 10px", borderRadius: 20, cursor: "pointer",
            fontSize: 10, fontWeight: 600, fontFamily: "JetBrains Mono, monospace",
            border: `1px solid ${color}50`,
            background: active ? `${color}20` : "transparent",
            color: textC, transition: "all .15s",
          }}>
            {PESTEL_ICONS[cat]} {cat}
          </button>
        );
      })}

      {hasFilters && (
        <button onClick={clearAll} style={{
          marginLeft: "auto", padding: "4px 10px", borderRadius: 20,
          fontSize: 10, cursor: "pointer", border: "1px solid #e2e5e4",
          background: "transparent", color: "#8E9A98", transition: "all .15s",
        }}>
          ✕ Limpiar
        </button>
      )}
    </div>
  );
}

// ── Main TrendRadar ───────────────────────────────────────────────────────
export default function TrendRadar({ signals = [], width = 620, height = 560 }) {
  const svgRef                      = useRef(null);
  const [selected, setSelected]     = useState(null);
  const [filters, setFilters]       = useState({ pestel: [], tipo: [] });

  const signalPositions = useMemo(() => {
  const cx   = width / 2;
  const cy   = height / 2;
  const qw   = cx - 60; // ancho de cada cuadrante
  const qh   = cy - 60; // alto de cada cuadrante

  const validCoords = signals.filter(s =>
    s.umap_x && s.umap_x !== 'NA' && !isNaN(parseFloat(s.umap_x)) &&
    s.umap_y && s.umap_y !== 'NA' && !isNaN(parseFloat(s.umap_y))
  );
  const xVals = validCoords.map(s => parseFloat(s.umap_x));
  const yVals = validCoords.map(s => parseFloat(s.umap_y));
  const xMin = Math.min(...xVals), xMax = Math.max(...xVals);
  const yMin = Math.min(...yVals), yMax = Math.max(...yVals);

  return signals.map((sig, i) => {
    const isOpp  = sig.classification === "Oportunidad";
    const impact = Math.max(0.05, Math.min(0.95, +sig.impact_probability || 0.1));
    const isLong = sig.time_horizon === "Largo plazo";
    const isMed  = sig.time_horizon === "Mediano plazo";

    const ux = parseFloat(sig.umap_x);
    const uy = parseFloat(sig.umap_y);

    // Jitter UMAP dentro del cuadrante
    let jx = 0, jy = 0;
    if (!isNaN(ux) && xMax > xMin) {
      jx = ((ux - xMin) / (xMax - xMin) - 0.5) * qw * 0.75;
    } else {
      jx = (((i * 137.5) % 90) / 90 - 0.5) * qw * 0.75;
    }
    if (!isNaN(uy) && yMax > yMin) {
      jy = ((uy - yMin) / (yMax - yMin) - 0.5) * qh * 0.5;
    } else {
      jy = (Math.sin(i * 1.618) * 0.5) * qh * 0.5;
    }

    // Cuadrante X: Oportunidad derecha, Riesgo izquierda
    const xBase = isOpp ? qw * 0.5 : -qw * 0.5;
    // Cuadrante Y: Largo plazo arriba, Corto plazo abajo
    const yBase = isLong ? -qh * 0.5 : isMed ? 0 : qh * 0.5;

    const x = xBase + jx;
    const y = yBase + jy;

    return { sig, x, y, impact, isOpp };
  });
}, [signals, width, height]);

  // Filtrado usando las posiciones ya calculadas
  const filteredPositions = signalPositions.filter(({ sig }) => {
    const pestelOk = !filters.pestel?.length || filters.pestel.includes(sig.pestel_category);
    const tipoOk   = !filters.tipo?.length   || filters.tipo.includes(sig.classification);
    return pestelOk && tipoOk;
  });

  const hiddenPositions = signalPositions.filter(({ sig }) => {
    const pestelOk = !filters.pestel?.length || filters.pestel.includes(sig.pestel_category);
    const tipoOk   = !filters.tipo?.length   || filters.tipo.includes(sig.classification);
    return !(pestelOk && tipoOk);
  });

  const draw = useCallback(() => {
  if (!svgRef.current) return;
  const svg  = d3.select(svgRef.current);
  svg.selectAll("*").remove();
  const cx   = width / 2;
  const cy   = height / 2;
  const qw   = cx - 60;
  const qh   = cy - 60;
  const g    = svg.append("g").attr("transform", `translate(${cx},${cy})`);

  // Fondos de cuadrantes
  const quads = [
    { x: 0,   y: -qh, w: qw,  h: qh,  fill: "rgba(53,98,122,0.04)"  }, // Oportunidad Largo
    { x: -qw, y: -qh, w: qw,  h: qh,  fill: "rgba(180,98,88,0.04)"  }, // Riesgo Largo
    { x: 0,   y: 0,   w: qw,  h: qh,  fill: "rgba(53,98,122,0.02)"  }, // Oportunidad Corto
    { x: -qw, y: 0,   w: qw,  h: qh,  fill: "rgba(180,98,88,0.02)"  }, // Riesgo Corto
  ];
  quads.forEach(q => {
    g.append("rect")
      .attr("x", q.x).attr("y", q.y)
      .attr("width", q.w).attr("height", q.h)
      .attr("fill", q.fill)
      .attr("stroke", "#e2e5e4").attr("stroke-width", 1);
  });

  // Ejes
  g.append("line").attr("x1", -qw).attr("y1", 0).attr("x2", qw).attr("y2", 0)
    .attr("stroke", "#c8cecc").attr("stroke-width", 1.5);
  g.append("line").attr("x1", 0).attr("y1", -qh).attr("x2", 0).attr("y2", qh)
    .attr("stroke", "#c8cecc").attr("stroke-width", 1.5);

  // Labels cuadrantes
  [
    { x: qw * 0.5,  y: -qh - 4,   text: "OPORTUNIDAD · LARGO PLAZO",  color: "#264a5e" },
    { x: -qw * 0.5, y: -qh - 4,   text: "RIESGO · LARGO PLAZO",       color: "#8a3f35" },
    { x: qw * 0.5,  y: qh + 14,   text: "OPORTUNIDAD · CORTO PLAZO",  color: "#264a5e" },
    { x: -qw * 0.5, y: qh + 14,   text: "RIESGO · CORTO PLAZO",       color: "#8a3f35" },
  ].forEach(q => {
    g.append("text")
      .attr("x", q.x).attr("y", q.y)
      .attr("text-anchor", "middle")
      .attr("fill", q.color).attr("font-size", 8).attr("font-weight", "700")
      .attr("font-family", "JetBrains Mono, monospace").attr("letter-spacing", 0.8)
      .attr("opacity", 0.7)
      .text(q.text);
  });

  // Labels ejes
  g.append("text").attr("x", 8).attr("y", -qh + 14)
    .attr("fill", "#8E9A98").attr("font-size", 8)
    .attr("font-family", "JetBrains Mono, monospace").text("↑ Largo plazo");
  g.append("text").attr("x", 8).attr("y", qh - 6)
    .attr("fill", "#8E9A98").attr("font-size", 8)
    .attr("font-family", "JetBrains Mono, monospace").text("↓ Corto plazo");
  g.append("text").attr("x", qw - 4).attr("y", -6)
    .attr("fill", "#264a5e").attr("font-size", 8).attr("text-anchor", "end")
    .attr("font-family", "JetBrains Mono, monospace").text("Oportunidad →");
  g.append("text").attr("x", -qw + 4).attr("y", -6)
    .attr("fill", "#8a3f35").attr("font-size", 8)
    .attr("font-family", "JetBrains Mono, monospace").text("← Riesgo");

  // Señales filtradas (grises)
  hiddenPositions.forEach(({ x, y, impact }) => {
    g.append("circle")
      .attr("transform", `translate(${x},${y})`)
      .attr("r", 4 + impact * 7)
      .attr("fill", "#e2e5e4").attr("fill-opacity", 0.3)
      .attr("stroke", "#c8cecc").attr("stroke-width", 1);
  });

  // Señales activas
  filteredPositions.forEach(({ sig, x, y, impact, isOpp }) => {
    const color      = PESTEL_COLORS[sig.pestel_category] || "#35627A";
    const radius     = 4 + impact * 7;
    const isSelected = selected?.Title === sig.Title;
    const node = g.append("g")
      .attr("transform", `translate(${x},${y})`)
      .style("cursor", "pointer");

    if (isSelected) {
      node.append("circle")
        .attr("r", radius + 5).attr("fill", "none")
        .attr("stroke", color).attr("stroke-width", 2).attr("stroke-opacity", 0.5);
    }
    node.append("circle")
      .attr("r", radius)
      .attr("fill", color)
      .attr("fill-opacity", isSelected ? 0.7 : isOpp ? 0.35 : 0.2)
      .attr("stroke", color)
      .attr("stroke-width", isSelected ? 2 : 1.5)
      .on("click", (event) => { event.stopPropagation(); setSelected(sig); })
      .on("mouseover", function() { d3.select(this).attr("fill-opacity", 0.6); })
      .on("mouseout",  function() { d3.select(this).attr("fill-opacity", isSelected ? 0.7 : isOpp ? 0.35 : 0.2); });

    node.append("title").text(
      `${sig.Title}\n${sig.pestel_category} · ${sig.classification}\nImpacto: ${Math.round(impact * 100)}%\n\nClic para ver detalle`
    );
  });

}, [filteredPositions, hiddenPositions, selected, width, height]);

  useEffect(() => { draw(); }, [draw]);

  useEffect(() => {
    const handler = () => setSelected(null);
    if (selected) document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [selected]);

  const activeCount = filteredPositions.length;
  const totalCount  = signals.length;

  return (
    <div onClick={e => e.stopPropagation()}>
      {signals.length > 0 && (
        <FilterBar activeFilters={filters} onFilterChange={setFilters} />
      )}

      {(filters.pestel?.length || filters.tipo?.length) > 0 && (
        <div style={{ fontSize: 11, color: "#8E9A98", marginBottom: 8, fontFamily: "JetBrains Mono, monospace" }}>
          Mostrando {activeCount} de {totalCount} señales
        </div>
      )}

      <div style={{ position: "relative" }}>
        <svg ref={svgRef} width={width} height={height}
          style={{ background: "#ffffff", borderRadius: 10, display: "block", margin: "0 auto", border: "1px solid #e2e5e4" }}
        />
      </div>

      {selected && (
        <>
          <div onClick={() => setSelected(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.15)", zIndex: 999 }}
          />
          <SignalPanel signal={selected} onClose={() => setSelected(null)} />
        </>
      )}
    </div>
  );
}
